import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { useToast } from '@/composables/useToast';
import { defineStore } from 'pinia';
import { Api } from '@/Api';
import { useViewerItemStore } from './useViewerItemStore';

export enum AiMetadataStatus {
  PENDING = 'pending',
  WORKING = 'working',
  DONE = 'done',
  FAILED = 'failed',
}

export interface AiChapters {
  title: string;
  timecode: string;
  startTime: number;
}

// AI Playback (SW-6793): word-level timestamps derived from the transcription
// provider's per-word results, used to skip filler words / silences during
// playback. `word` is the matched filler word/phrase, lowercased.
export interface AiFillerWord {
  start: number;
  end: number;
  word: string;
}

export interface AiSilence {
  start: number;
  end: number;
}

export interface AiMetadataStoreState {
  status?: AiMetadataStatus;
  chapters?: AiChapters[];
  provider?: string;
  feedback?: 'positive' | 'negative' | null;
  createdAt?: string;
  updatedAt?: string;
  fillerWords?: AiFillerWord[];
  silences?: AiSilence[];
  // Server-persisted, not a per-viewer preference: the owner's choice here is
  // what everyone's playback (owner or guest) respects, and it survives a
  // reload — see useAiPlaybackStore for the (unpersisted) live skip counters.
  skipFillerWords: boolean;
  skipSilences: boolean;
}

const { debug } = useDebug(DEBUG_NAMESPACE.STORE_AI_METADATA);

export const useAiMetadataStore = defineStore('aiMetadataStore', {
  state: () =>
    ({
      chapters: undefined,
      status: undefined,
      provider: undefined,
      feedback: null,
      createdAt: undefined,
      updatedAt: undefined,
      fillerWords: undefined,
      silences: undefined,
      skipFillerWords: false,
      skipSilences: false,
    }) as AiMetadataStoreState,
  actions: {
    // Initialize viewer from application store initializer
    initialize(aiMetadata: any) {
      debug('[initialize] Initialize AIMetadata store', aiMetadata);
      if (aiMetadata) {
        this.setAiMetadata(aiMetadata);
      }
    },
    patchAIMetaData(data: any) {
      if (data && data.attributes) {
        this.$patch({
          ...data.attributes,
        });
      }
    },
    // The ai_metadata_update Pusher event carries a lightweight payload with
    // no filler_words/silences (see ItemAiMetadataPusherSerializer, kept
    // under Pusher's payload size limit) — refetch the full resource once
    // that event fires so Skip Filler Words/Silences can turn on without a
    // manual reload for a video still open when AI processing finishes.
    async fetchFullMetadata() {
      const itemStore = useViewerItemStore();
      const requestedId = itemStore.id;
      if (!requestedId) return;

      // Fired from a Pusher event on the item channel, which every viewer of
      // a shared item receives — a stale password/security token or a since-
      // disabled item can make this legitimately fail, so don't let it
      // surface as an unhandled rejection.
      try {
        const res = await Api.item.aiMetadata.get(requestedId);
        if (itemStore.id !== requestedId) return;

        if (res?.data?.itemAiMetadatum) {
          this.patchAIMetaData(res.data.itemAiMetadatum);
        }
      } catch (error) {
        debug('[fetchFullMetadata] Failed to refetch AI metadata', error);
      }
    },
    setAiMetadata(aiMetadata: any) {
      if (!aiMetadata.data.attributes) {
        debug('Cannot set AI metadata data', aiMetadata);
        return;
      }

      this.$patch({
        ...aiMetadata.data.attributes,
      });
    },
    async useDefaultSummary() {
      const itemStore = useViewerItemStore();

      if (!itemStore.id) {
        return Promise.resolve();
      }

      const res = await Api.item.aiMetadata.patch(itemStore.id);
      if (res && res.data && res.data.item) {
        itemStore.setItem(res.data.item);
      }
    },
    setAiMetadataFeedback(feedback: 'positive' | 'negative' | null) {
      this.$patch({ feedback });
    },
    // useAiPlaybackEffect (the seek watcher) stays mounted across item
    // navigation in Video.vue, so without this a video with Skip Filler
    // Words on would keep seeking against the *previous* item's ranges
    // until something else (a Pusher event, a manual fetch) overwrites this
    // store — clear synchronously, then fetchFullMetadata refills it for
    // whichever item is now current.
    resetForNewItem() {
      this.$patch({
        chapters: undefined,
        status: undefined,
        provider: undefined,
        feedback: null,
        createdAt: undefined,
        updatedAt: undefined,
        fillerWords: undefined,
        silences: undefined,
        skipFillerWords: false,
        skipSilences: false,
      });
    },
    // Owner-only (enforced server-side) — persists so it survives a reload
    // and, via the model's ai_metadata_update Pusher event, applies to any
    // guest already watching. Applied optimistically since it's a checkbox
    // the owner is actively toggling; rolled back if the save fails.
    async updateSkipSettings(settings: { skipFillerWords?: boolean; skipSilences?: boolean }) {
      const itemStore = useViewerItemStore();
      if (!itemStore.id) return;

      // Scoped to just the keys being changed — snapshotting both would let
      // a rollback from a failed save clobber a *different* toggle back to
      // its pre-change value if the two were flipped in quick succession.
      const previous: typeof settings = {};
      if ('skipFillerWords' in settings) previous.skipFillerWords = this.skipFillerWords;
      if ('skipSilences' in settings) previous.skipSilences = this.skipSilences;
      this.$patch(settings);

      try {
        await Api.item.aiMetadata.updateSkipSettings(itemStore.id, settings);
      } catch (error) {
        debug('[updateSkipSettings] Failed to save, rolling back', error);
        this.$patch(previous);
        useToast().error("Couldn't save your AI Playback setting. Please try again.");
      }
    },
  },
  getters: {
    hasChapters: (state: AiMetadataStoreState) => !!(state.chapters && state.chapters.length > 0),
    isProcessing: (state: AiMetadataStoreState) => state.status === AiMetadataStatus.PENDING || state.status === AiMetadataStatus.WORKING,
    fillerWordsCount: (state: AiMetadataStoreState) => state.fillerWords?.length ?? 0,
    fillerWordsTotalSeconds: (state: AiMetadataStoreState) => (state.fillerWords ?? []).reduce((total, word) => total + (word.end - word.start), 0),
    silencesCount: (state: AiMetadataStoreState) => state.silences?.length ?? 0,
    silencesTotalSeconds: (state: AiMetadataStoreState) => (state.silences ?? []).reduce((total, silence) => total + (silence.end - silence.start), 0),
  },
});
